import React from "react";
import { useSearchParams } from "react-router-dom";
import "./PartnerProgramSettings.scss";
import {
  BreadcrumbItem,
  Breadcrumbs,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import { Typography, Tabs, Tab } from "@mui/material";
import PartnerTierManagement from "./partner-program/tier-management";
import ValidationManagement from "./validation-management";
import TechnologyManagement from "./technology-management";
import UserManagement from "../user-management";
import { useAppSelector } from "../../../../redux";
import PartnerTypeVerticalSettings from "./components/PartnerTypeVerticalSettings";
import {
  useSessionModuleDenied,
  useSessionModuleReadOnly,
  useSessionPermissionAction,
  useSessionPermissionsLoaded,
  useSessionPermissionsLoading,
} from "../../../../hooks/useSessionPermissions";
import { SETTINGS_PERMISSION_KEYS } from "../../../../utils/permissions/settingsPermissionKeys";

interface SettingsCard {
  id: string;
  icon: string;
  name: string;
}

const cards: SettingsCard[] = [
  { id: "user-management", icon: "userGroup", name: "User Management" },
  { id: "partner-settings", icon: "gear", name: "Partner Settings" },
  { id: "partner-program", icon: "awardSimple", name: "Partner Program" },
  {
    id: "validation-management",
    icon: "circleCheck",
    name: "Validation Management",
  },
  {
    id: "technology-management",
    icon: "networkWired",
    name: "Technology Management",
  }
  // { id: "certification", icon: "medal", name: "Certification" },
  // { id: "email-templates", icon: "envelop", name: "Email Templates" },
  // {
  //   id: "content-management",
  //   icon: "memoCircleCheck",
  //   name: "Content Management",
  // },
  // { id: "case-management", icon: "bookBookmark", name: "Case Management" },
  // { id: "service-menu", icon: "bars", name: "Service Menu" },
];

const iconTheme: Record<string, { fg: string; bg: string }> = {
  "user-management": { fg: "#155DFC", bg: "#EFF6FF" },
  "partner-settings": { fg: "#155DFC", bg: "#EFF6FF" },
  "partner-program": { fg: "#9810FA", bg: "#FAF5FF" },
  "validation-management": { fg: "#2ECC71", bg: "#EFFFF3" },
  "technology-management": { fg: "#0891B2", bg: "#ECFEFF" },
  certification: { fg: "#D08700", bg: "#FFF4E8" },
  "email-templates": { fg: "#EB5757", bg: "#FFF1F1" },
  "content-management": { fg: "#4F39F6", bg: "#EEF2FF" },
  "case-management": { fg: "#F54900", bg: "#FFF7ED" },
  "service-menu": { fg: "#E60076", bg: "#FDF2F8" },
};

const IconsList: React.FC<{
  name: string;
  className?: string;
  size?: string;
}> = ({ name, className, size }) => {
  try {
    return <Iconography icon={name} className={className} size={size} />;
  } catch {
    return <Iconography icon="Menu" className={className} size={size} />;
  }
};

type ViewMode = "cards" | "module";

const PARTNER_PROGRAM_TABS = [
  {
    sourceIndex: 0,
    label: "Partner Setting",
    permissionKey: "partner_type_vertical_settings",
  },
  { sourceIndex: 1, label: "Tier Management", permissionKey: "tier_management" },
  { sourceIndex: 2, label: "License Management", permissionKey: "license_management" },
  { sourceIndex: 3, label: "Regional Modifier", permissionKey: "regional_modifer" },
  {
    sourceIndex: 4,
    label: "Engagement Category",
    permissionKey: "engagement_category",
  },
  {
    sourceIndex: 5,
    label: "Engagement Configuration",
    permissionKey: "engagement_configuration",
  },
  { sourceIndex: 6, label: "Benefits", permissionKey: "benefits_configuration" }
  // { sourceIndex: 7, label: "Booster Package", permissionKey: "boost_package_settings" },
] as const;

const MODULE_META: Record<string, { title: string; subtitle: string }> = {
  "partner-program": {
    title: "Partner Program Configuration",
    subtitle: "Manage partner program settings and regional multipliers",
  },
  "partner-settings": {
    title: "Partner Settings",
    subtitle: "Manage partner type and partner vertical settings",
  },
  "user-management": {
    title: "User management",
    subtitle: "Configure access levels and permission sets",
  },
  "validation-management": {
    title: "Validation Management",
    subtitle: "Manage validation workflows and rules",
  },
  "technology-management": {
    title: "Technology Management",
    subtitle: "Manage Technology settings and Integrations",
  },
  certification: {
    title: "Certification",
    subtitle: "Define certification tiers and criteria",
  },
  "email-templates": {
    title: "Email Templates",
    subtitle: "Create and manage email templates",
  },
  "content-management": {
    title: "Content Management",
    subtitle: "Manage pages, assets, and content publishing",
  },
  "case-management": {
    title: "Case Management",
    subtitle: "View and manage partner cases",
  },
  "service-menu": {
    title: "Service Menu",
    subtitle: "Configure available services",
  },
};

const UM_PERM = SETTINGS_PERMISSION_KEYS.USER_SETTINGS;
const PP_PERM = SETTINGS_PERMISSION_KEYS.PARTNER_PROGRAM_SETTINGS;
const PS_PERM = SETTINGS_PERMISSION_KEYS.PARTNER_MANAGEMENT_SETTINGS;
const VM_PERM = SETTINGS_PERMISSION_KEYS.VALIDATION_SETTINGS;
const TM_PERM = SETTINGS_PERMISSION_KEYS.TECHNOLOGY_SETTINGS;

const PartnerProgramSettings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = React.useState<ViewMode>("cards");
  const [selectedModule, setSelectedModule] = React.useState<string | null>(
    null
  );
  const [hideModuleHeader, setHideModuleHeader] = React.useState(false);
  const [ppTab, setPpTab] = React.useState<number>(0);
  const loggedUserDetail = useAppSelector(
    (state) => state.auth.loggedUserDetail
  );

  const sessionPermissionsLoading = useSessionPermissionsLoading();
  const sessionPermissionsLoaded = useSessionPermissionsLoaded();
  const ppPermissionAction = useSessionPermissionAction(PP_PERM);
  const partnerProgramReadOnly = useSessionModuleReadOnly(PP_PERM);
  const partnerProgramDenied = useSessionModuleDenied(PP_PERM);

  const psPermissionAction = useSessionPermissionAction(PS_PERM);
  const partnerSettingsReadOnly = useSessionModuleReadOnly(PS_PERM);
  const partnerSettingsDenied = useSessionModuleDenied(PS_PERM);

  const vmPermissionAction = useSessionPermissionAction(VM_PERM);
  const validationMgmtReadOnly = useSessionModuleReadOnly(VM_PERM);
  const validationMgmtDenied = useSessionModuleDenied(VM_PERM);

  const umPermissionAction = useSessionPermissionAction(UM_PERM);
  const userMgmtReadOnly = useSessionModuleReadOnly(UM_PERM);
  const userMgmtDenied = useSessionModuleDenied(UM_PERM);

  const tmPermissionAction = useSessionPermissionAction(TM_PERM);
  const technologyMgmtReadOnly = useSessionModuleReadOnly(TM_PERM);
  const technologyMgmtDenied = useSessionModuleDenied(TM_PERM);

  /** When `role_id` is missing (legacy env), keep the page visible but avoid hardcoded role checks. */
  const fallbackCardIdsWithoutRoleId = React.useMemo(
    () => cards.map((item) => item.id),
    []
  );

  const roleId = loggedUserDetail?.role_id;
  const sessionGateReady =
    Boolean(roleId) &&
    sessionPermissionsLoaded &&
    !sessionPermissionsLoading;

  const partnerProgramAllowed = React.useMemo(() => {
    if (!roleId) return fallbackCardIdsWithoutRoleId.includes("partner-program");
    if (!sessionGateReady) return false;
    return ppPermissionAction === "edit" || ppPermissionAction === "view";
  }, [
    roleId,
    sessionGateReady,
    fallbackCardIdsWithoutRoleId,
    ppPermissionAction,
  ]);

  const partnerSettingsAllowed = React.useMemo(() => {
    if (!roleId) return fallbackCardIdsWithoutRoleId.includes("partner-settings");
    if (!sessionGateReady) return false;
    return psPermissionAction === "edit" || psPermissionAction === "view";
  }, [
    roleId,
    sessionGateReady,
    fallbackCardIdsWithoutRoleId,
    psPermissionAction,
  ]);

  const validationMgmtAllowed = React.useMemo(() => {
    if (!roleId) {
      return fallbackCardIdsWithoutRoleId.includes("validation-management");
    }
    if (!sessionGateReady) return false;
    return vmPermissionAction === "edit" || vmPermissionAction === "view";
  }, [
    roleId,
    sessionGateReady,
    fallbackCardIdsWithoutRoleId,
    vmPermissionAction,
  ]);

  const userMgmtAllowed = React.useMemo(() => {
    if (!roleId) return fallbackCardIdsWithoutRoleId.includes("user-management");
    if (!sessionGateReady) return false;
    return umPermissionAction === "edit" || umPermissionAction === "view";
  }, [
    roleId,
    sessionGateReady,
    fallbackCardIdsWithoutRoleId,
    umPermissionAction,
  ]);

  const technologyMgmtAllowed = React.useMemo(() => {
    if (!roleId) {
      return fallbackCardIdsWithoutRoleId.includes("technology-management");
    }
    if (!sessionGateReady) return false;
    return tmPermissionAction === "edit" || tmPermissionAction === "view";
  }, [
    roleId,
    sessionGateReady,
    fallbackCardIdsWithoutRoleId,
    tmPermissionAction,
  ]);

  const visibleCards = React.useMemo(() => {
    return cards.filter((item) => {
      if (item.id === "technology-management") return technologyMgmtAllowed;
      if (item.id === "partner-program") return partnerProgramAllowed;
      if (item.id === "partner-settings") return partnerSettingsAllowed;
      if (item.id === "validation-management") return validationMgmtAllowed;
      if (item.id === "user-management") return userMgmtAllowed;
      return false;
    });
  }, [
    partnerProgramAllowed,
    partnerSettingsAllowed,
    validationMgmtAllowed,
    userMgmtAllowed,
    technologyMgmtAllowed,
  ]);

  const settingsPermissionsBlocking =
    Boolean(roleId) && (sessionPermissionsLoading || !sessionPermissionsLoaded);

  React.useEffect(() => {
    if (!loggedUserDetail?.role_id) return;
    if (sessionPermissionsLoading || !sessionPermissionsLoaded) return;

    const denied =
      (selectedModule === "partner-program" && partnerProgramDenied) ||
      (selectedModule === "partner-settings" && partnerSettingsDenied) ||
      (selectedModule === "validation-management" && validationMgmtDenied) ||
      (selectedModule === "user-management" && userMgmtDenied) ||
      (selectedModule === "technology-management" && technologyMgmtDenied);

    if (denied) {
      setSelectedModule(null);
      setView("cards");
      setHideModuleHeader(false);
    }
  }, [
    selectedModule,
    loggedUserDetail?.role_id,
    sessionPermissionsLoading,
    sessionPermissionsLoaded,
    partnerProgramDenied,
    partnerSettingsDenied,
    validationMgmtDenied,
    userMgmtDenied,
    technologyMgmtDenied,
  ]);

  const visiblePartnerProgramTabs = React.useMemo(() => {
    return PARTNER_PROGRAM_TABS;
  }, []);

  const selectedPpTabUiIndex = React.useMemo(() => {
    const tabIndex = visiblePartnerProgramTabs.findIndex(
      (tab) => tab.sourceIndex === ppTab
    );
    return tabIndex >= 0 ? tabIndex : 0;
  }, [ppTab, visiblePartnerProgramTabs]);

  React.useEffect(() => {
    if (selectedModule !== "partner-program" || !visiblePartnerProgramTabs.length) {
      return;
    }
    const hasSelectedTab = visiblePartnerProgramTabs.some(
      (tab) => tab.sourceIndex === ppTab
    );
    if (!hasSelectedTab) {
      setPpTab(visiblePartnerProgramTabs[0].sourceIndex);
    }
  }, [ppTab, selectedModule, visiblePartnerProgramTabs]);

  const openModule = (id: string) => {
    if (id === "partner-program" && !partnerProgramAllowed) return;
    if (id === "partner-settings" && !partnerSettingsAllowed) return;
    if (id === "validation-management" && !validationMgmtAllowed) return;
    if (id === "user-management" && !userMgmtAllowed) return;
    if (id === "technology-management" && !technologyMgmtAllowed) return;
    setSelectedModule(id);
    setView("module");
    setHideModuleHeader(false);
    if (id === "partner-program") {
      setPpTab(0);
    }
    if (searchParams.get("module")) {
      setSearchParams({}, { replace: true });
    }
  };

  React.useEffect(() => {
    const moduleParam = searchParams.get("module");
    if (!moduleParam || view === "module") return;
    if (moduleParam === "user-management" && userMgmtAllowed) {
      setSelectedModule("user-management");
      setView("module");
      setHideModuleHeader(false);
    }
    if (moduleParam === "technology-management" && technologyMgmtAllowed) {
      setSelectedModule("technology-management");
      setView("module");
      setHideModuleHeader(false);
    }
  }, [searchParams, view, userMgmtAllowed, technologyMgmtAllowed]);

  const backToCards = () => {
    setSelectedModule(null);
    setView("cards");
    setHideModuleHeader(false);
  };

  const handleRolesPermissionsDetailViewChange = React.useCallback(
    (isDetailView: boolean) => {
      setHideModuleHeader(isDetailView);
    },
    []
  );

  const renderModule = () => {
    switch (selectedModule) {
      case "partner-program":
        if (
          loggedUserDetail?.role_id &&
          sessionPermissionsLoaded &&
          partnerProgramDenied
        ) {
          return (
            <div className="pp-module-panel" role="status">
              You do not have permission to view Partner Program configuration for
              your role.
            </div>
          );
        }
        return (
          <div className="pp-module-left">
            {visiblePartnerProgramTabs.length > 0 ? (
              <Tabs
                value={selectedPpTabUiIndex}
                onChange={(_, uiTabIndex) =>
                  setPpTab(visiblePartnerProgramTabs[uiTabIndex].sourceIndex)
                }
                aria-label="Partner Program Tabs"
                className="pp-config-tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                {visiblePartnerProgramTabs.map((tab) => (
                  <Tab key={tab.permissionKey} label={tab.label} />
                ))}
              </Tabs>
            ) : (
              <div className="pp-module-panel">
                You do not have access to any Partner Program Settings tab.
              </div>
            )}

            <div className="pp-module-panel">
              {selectedModule === "partner-program" &&
                visiblePartnerProgramTabs.length > 0 && (
                <PartnerTierManagement
                  activeTab={ppTab}
                  onTabChange={(v) => setPpTab(v)}
                  readOnly={partnerProgramReadOnly}
                />
                )}
            </div>
          </div>
        );
      case "validation-management":
        if (
          loggedUserDetail?.role_id &&
          sessionPermissionsLoaded &&
          validationMgmtDenied
        ) {
          return (
            <div className="pp-module-panel" role="status">
              You do not have permission to view Validation Management for your
              role.
            </div>
          );
        }
        return (
          <ValidationManagement readOnly={validationMgmtReadOnly} />
        );
      case "technology-management":
        if (
          loggedUserDetail?.role_id &&
          sessionPermissionsLoaded &&
          technologyMgmtDenied
        ) {
          return (
            <div className="pp-module-panel" role="status">
              You do not have permission to view Technology Management for your
              role.
            </div>
          );
        }
        return <TechnologyManagement readOnly={technologyMgmtReadOnly} />;
      case "user-management":
        if (
          loggedUserDetail?.role_id &&
          sessionPermissionsLoaded &&
          userMgmtDenied
        ) {
          return (
            <div className="pp-module-panel" role="status">
              You do not have permission to view User Management for your role.
            </div>
          );
        }
        return (
          <UserManagement
            onDetailViewChange={handleRolesPermissionsDetailViewChange}
            readOnly={userMgmtReadOnly}
          />
        );
      case "partner-settings":
        if (
          loggedUserDetail?.role_id &&
          sessionPermissionsLoaded &&
          partnerSettingsDenied
        ) {
          return (
            <div className="pp-module-panel" role="status">
              You do not have permission to view Partner Settings for your role.
            </div>
          );
        }
        return (
          <PartnerTypeVerticalSettings readOnly={partnerSettingsReadOnly} />
        );
      default:
        return null;
    }
  };

  if (view === "module" && selectedModule) {
    const meta = MODULE_META[selectedModule] ?? {
      title: "Settings",
      subtitle: "",
    };

    const hideBreadcrumbs =
      selectedModule === "user-management" && hideModuleHeader;

    return (
      <div className="pp-settings-page">
        {!hideBreadcrumbs && (
          <Breadcrumbs noTrailingSlash={true}>
            <BreadcrumbItem
              name="Settings"
              onClick={() => {
                setView("cards");
                setSelectedModule(null);
              }}
              breadcrumbSeparator={<Iconography icon="chevronRight" />}
              isCurrentPage={false}
            />
            <BreadcrumbItem
              name={meta.title}
              breadcrumbSeparator={<Iconography icon="chevronRight" />}
              isCurrentPage={true}
            />
          </Breadcrumbs>
        )}

        {!hideBreadcrumbs && (
          <>
            <Typography
              style={{
                fontWeight: 500,
                fontFamily: "Roboto",
                fontStyle: "normal",
                fontSize: 26,
                lineHeight: "30px",
                letterSpacing: "0.2px",
                color: "#222222",
              }}
            >
              {meta.title}
            </Typography>

            {meta.subtitle && (
              <Typography
                variant="h2"
                style={{
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontStyle: "normal",
                  fontSize: 18,
                  lineHeight: "22px",
                  letterSpacing: "0.2px",
                  color: "#4A5565",
                }}
              >
                {meta.subtitle}
              </Typography>
            )}
          </>
        )}

        <div className="pp-module-content">
          {renderModule()}
          <div className="pp-right-rail">
            <h3 className="pp-qa-heading" style={{ margin: "20px 0px" }}>
              Quick Actions
            </h3>
            <aside className="pp-quick-actions" aria-label="Quick actions">
              {visibleCards.map((item) => {
                return (
                  <div
                    key={item.id}
                    className="pp-qa-item"
                    role="button"
                    tabIndex={0}
                    aria-label={`Open ${item.name}`}
                    onClick={() => openModule(item.id)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openModule(item.id);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <span
                      className="pp-card-quick-icon"
                      style={{ color: "#003087" }}
                    >
                      <IconsList
                        name={item.icon}
                        className="icon"
                        size={IconographySizes.Medium}
                      />
                    </span>
                    <span className="pp-qa-title">{item.name}</span>
                    <Iconography icon="arrowUpRight" className="pp-qa-arrow" />
                  </div>
                );
              })}
            </aside>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pp-settings-page">
      <div className="pp-settings-header">
        <h1>Settings</h1>
        <h2>Manage your portal preferences and configurations</h2>
      </div>
      <section className="pp-settings-grid">
        {settingsPermissionsBlocking && (
          <div className="pp-module-panel" role="status">
            Loading permissions…
          </div>
        )}
        {!settingsPermissionsBlocking && visibleCards.length === 0 && (
          <div className="pp-module-panel" role="status">
            You do not have access to any settings modules for your role.
          </div>
        )}
        {!settingsPermissionsBlocking &&
          visibleCards.map((item) => {
          const theme = iconTheme[item.id];
          const interactiveProps = {
            role: "button" as const,
            tabIndex: 0,
            onClick: () => openModule(item.id),
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openModule(item.id);
              }
            },
            "aria-label": `Open ${item.name}`,
            style: { cursor: "pointer" as const },
            className: "pp-settings-card clickable",
          };

          return (
            <div key={item.id} {...interactiveProps}>
              <div className="pp-settings-card-container">
                <div className="pp-card-left">
                  <span
                    className="pp-card-icon"
                    style={{ backgroundColor: theme.bg, color: theme.fg }}
                  >
                    <IconsList
                      name={item.icon}
                      className="icon"
                      size={IconographySizes.Medium}
                    />
                  </span>
                  <Typography variant="h3" className="pp-card-title">
                    {item.name}
                  </Typography>
                </div>
                <Iconography icon="arrowRight" className="pp-card-arrow" />
              </div>
            </div>
          );
          })}
      </section>
    </div>
  );
};

export default PartnerProgramSettings;
